import { styled } from '@mui/material';

import {
  Card,
  Skeleton,
  SpeedometerChart,
  SpeedometerChartProps,
  Typography,
  Box,
  Stack,
  tokens,
} from '@cast/design-system';

import { MetricsBox } from 'components/boxes';

import { ResourceUsageMetrics } from '../../../../types';

type MetricCardProps = {
  title: string;
  acronym: string;
  metrics: ResourceUsageMetrics;
  metricsBoxed: {
    title: string;
    markerColor: string;
    primaryValue: number | string;
    secondaryValue: number | string;
  }[];
  outerLayer: SpeedometerChartProps['outerLayer'];
  testId?: string;
};

const MetricsCardConainer = styled(Stack)({
  gap: 8,
  flex: 1,
  minWidth: 370,
  maxWidth: 'min(542px, calc((100% - 16px) / 2))',
});

const MetricCardSkeleton = () => (
  <MetricsCardConainer>
    <Skeleton height={16} width={60} />
    <Card sx={{ p: 16, flex: 1 }}>
      <Skeleton height={248} />
    </Card>
  </MetricsCardConainer>
);

export const MetricCard = ({
  title,
  acronym,
  metrics,
  metricsBoxed,
  outerLayer,
  testId,
}: MetricCardProps) => {
  return (
    <MetricsCardConainer>
      <Typography variant="A2" color={tokens.theme.fg.structural.secondary}>
        {title}
      </Typography>
      <Card
        sx={{
          p: 16,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1,
        }}
        data-testid={testId}
      >
        <Box flex={1} maxWidth={180}>
          <SpeedometerChart
            variant="normal"
            total={metrics.capacity}
            title={metrics.capacity}
            pieLabelProps={{
              sx: (theme) => ({ ...theme.typography.A1, fontSize: 18 }),
            }}
            subtitle={
              <Stack alignItems="center" mt={-11} lineHeight={24} gap={1}>
                <Typography variant="A1" fontSize={18}>
                  {acronym}
                </Typography>
                <Typography variant="L10R" color="textSecondary">
                  PROVISIONED
                </Typography>
              </Stack>
            }
            outerLayer={outerLayer}
            appendix={{
              color: 'grey.200',
              value:
                metrics.capacity !== 0
                  ? metrics.capacity - metrics.allocatable
                  : 0,
            }}
          />
        </Box>
        <Box display="flex" flexDirection="column" gap="16px" margin="auto">
          {metricsBoxed.map((metric) => (
            <MetricsBox
              key={metric.title}
              testId={`${testId}-${metric.title.toLowerCase()}`}
              markerColor={metric.markerColor}
              title={metric.title}
              primaryValue={metric.primaryValue}
              primaryValueProps={{ variant: 'h6' }}
              secondaryValue={metric.secondaryValue}
              secondaryValueProps={{
                variant: 'L12R',
                color: 'textSecondary',
              }}
            />
          ))}
        </Box>
      </Card>
    </MetricsCardConainer>
  );
};

MetricCard.Skeleton = MetricCardSkeleton;
